import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import {
  ROTAS,
  apiVersionSuspeita,
  novaLinhaDeModelo,
  type ModelEntry,
} from "@/features/Integrations/utils/modelEntries";

/**
 * O catálogo de modelos da instalação, editável (AB#85605).
 *
 * Ele não é decoração: dele saem a rota em que cada modelo responde (`api`), a `api_version`
 * que aquele modelo exige, e — no embedding — o tamanho do vetor. É o que decide se uma
 * chamada sai pelo endpoint certo ou volta um 404 que parece modelo inexistente.
 *
 * No Azure a chave é o nome do DEPLOYMENT, que quem criou o recurso escolheu — nenhum cadastro
 * pode adivinhá-lo, por isso é aqui que ele é declarado. Em provedores de catálogo fixo
 * (OpenAI, OpenRouter) é por aqui que se acrescenta um modelo novo sem esperar por atualização
 * de cadastro.
 *
 * O que a tela NÃO mostra fica preservado: preço (`cost_source`), provider e host por modelo
 * continuam gravados — o editor devolve as entradas com os campos que não edita intactos.
 */

export function IntegrationModelsEditor({
  entries,
  onChange,
}: {
  entries: ModelEntry[];
  onChange: (entries: ModelEntry[]) => void;
}) {
  const { t } = useTranslation();

  const alterar = (indice: number, patch: Partial<ModelEntry>) =>
    onChange(entries.map((entry, i) => (i === indice ? { ...entry, ...patch } : entry)));

  const remover = (indice: number) => onChange(entries.filter((_, i) => i !== indice));

  const adicionar = () => onChange([...entries, novaLinhaDeModelo()]);

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {/* Quem rola é a aba inteira, uma barra só: com a lista rolando por dentro, o
          diálogo mostrava duas barras lado a lado. */}
      {entries.length > 0 ? (
        <div className="space-y-2">
          {entries.map((entry, indice) => (
            <div
              key={entry.id}
              className="space-y-2 rounded-md border border-border/60 p-3"
            >
              <div className="flex items-center gap-2">
                <Input
                  value={entry.name}
                  onChange={(e) => alterar(indice, { name: e.target.value })}
                  placeholder={t("integrations.models.namePlaceholder")}
                  className="h-9 flex-1 font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => remover(indice)}
                  aria-label={t("integrations.models.remove")}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Select
                  value={entry.api || undefined}
                  onValueChange={(value) => alterar(indice, { api: value as ModelEntry["api"] })}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder={t("integrations.models.apiPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {ROTAS.map((rota) => (
                      <SelectItem key={rota.value} value={rota.value as string}>
                        {rota.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  value={entry.api_version ?? ""}
                  onChange={(e) => alterar(indice, { api_version: e.target.value })}
                  placeholder={t("integrations.models.apiVersionPlaceholder")}
                  // Aviso, não bloqueio: valor fora do padrão de data ainda salva, mas
                  // deixa de virar um 404 sem pista.
                  title={
                    apiVersionSuspeita(entry.api_version)
                      ? t("integrations.models.apiVersionSuspect")
                      : undefined
                  }
                  className={`h-9 font-mono text-xs ${
                    apiVersionSuspeita(entry.api_version) ? "border-amber-500/70" : ""
                  }`}
                />

                <Input
                  value={entry.dimensions ?? ""}
                  onChange={(e) => alterar(indice, { dimensions: e.target.value })}
                  placeholder={t("integrations.models.dimensionsPlaceholder")}
                  disabled={entry.api !== "embedding"}
                  // Obrigatório no embedding: marcado no próprio campo, que é onde a pessoa
                  // olha, em vez de só na mensagem de erro.
                  aria-invalid={entry.api === "embedding" && !entry.dimensions?.trim()}
                  className={`h-9 font-mono text-xs ${
                    entry.api === "embedding" && !entry.dimensions?.trim()
                      ? "border-destructive/60"
                      : ""
                  }`}
                />
              </div>

              {entry.model && entry.model !== entry.name ? (
                <span className="block text-[11px] text-muted-foreground">
                  {t("integrations.models.serves", { model: entry.model })}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        /* Vazio não ocupa a altura toda: o convite fica junto do botão, não flutuando no
           meio do diálogo. */
        <div className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center">
          <p className="text-sm text-muted-foreground">{t("integrations.models.empty")}</p>
        </div>
      )}

      {/* A `api-version` é a versão da API REST, e no Azure ela tem a MESMA cara da "model
          version" que o portal exibe no card do deployment (`2024-11-20`). Trocar uma pela
          outra devolve 404 "Resource not found" — a mesma resposta de deployment inexistente,
          que manda procurar no lugar errado. Medido nos recursos do dev: com `2024-11-20` o
          deployment `gpt-4o` responde 404; com `2024-10-21`, 200.

          O campo é por MODELO e não por instalação porque é assim que o provedor se comporta:
          no mesmo recurso, `/openai/responses` só atende a partir de `2025-04-01-preview`
          enquanto `/chat/completions` atende em `2024-10-21` (AB#84919). */}
      {entries.length > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          {t("integrations.models.apiVersionHint")}
        </p>
      ) : null}

      <Button type="button" variant="outline" size="sm" className="self-start" onClick={adicionar}>
        <Plus className="mr-2 h-4 w-4" />
        {t("integrations.models.add")}
      </Button>
    </div>
  );
}
